import React from 'react';
import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';

interface ProspectingDay {
  id: string;
  date: string;
  sdr_name: string;
}

interface NextProspectingDaysCardProps {
  days: ProspectingDay[];
  loading: boolean;
}

const NextProspectingDaysCard: React.FC<NextProspectingDaysCardProps> = ({ days, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-48 mb-6"></div>
        <div className="space-y-3">
          <div className="h-20 bg-gray-200 rounded-xl"></div>
          <div className="h-20 bg-gray-200 rounded-xl"></div>
          <div className="h-20 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (!days || days.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Prochaines journées de prospection</h3>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Calendar className="h-12 w-12 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">Aucune session planifiée</p>
        </div>
      </motion.div>
    );
  }

  const getDaysUntil = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Demain";
    if (diffDays < 7) return `Dans ${diffDays} jours`;
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Prochaines journées de prospection</h3>
        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
          {days.length} session{days.length > 1 ? 's' : ''}
        </span>
      </div>

      <div className="space-y-3">
        {days.slice(0, 5).map((day, index) => {
          const formattedDate = new Date(day.date).toLocaleDateString('fr-FR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          });

          const daysUntil = getDaysUntil(day.date);

          return (
            <motion.div
              key={day.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-200"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <p className="text-sm font-medium text-gray-900 capitalize">{formattedDate}</p>
                </div>

                {daysUntil && (
                  <span className="text-xs font-medium text-blue-700 bg-blue-50 px-3 py-1 rounded-full flex-shrink-0">
                    {daysUntil}
                  </span>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {days.length > 5 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500 text-center">
            +{days.length - 5} session{days.length - 5 > 1 ? 's' : ''} supplémentaire{days.length - 5 > 1 ? 's' : ''}
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default NextProspectingDaysCard;
